import { useEffect, useReducer } from 'react';

import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { createContainer } from 'unstated-next';
import { v4 as uuid } from 'uuid';

import { useHistory, useLocation } from 'react-router-dom';
import { AUTH_ME } from '../../config/apiPaths';
import { AuthMeResponse } from '../../types/apiTypes';
import { getUrls } from '../../utils/url';
import { createOauthState } from '../../utils/oauthState';
import { UserRole } from './constants/user-roles';

interface AuthState {
    isLoading: boolean;
    isLoggedIn: boolean;
    isAdmin: boolean;
    isSuperAdmin: boolean;
    userToken: string;
    user: AuthMeResponse | null;
}

const defaultState: AuthState = {
    isLoading: true,
    isLoggedIn: false,
    isAdmin: false,
    isSuperAdmin: false,
    userToken: '',
    user: null
};

const reducer = (state: AuthState, action) => {
    switch (action.type) {
        case 'login': {
            return {
                isLoading: false,
                isLoggedIn: true,
                isAdmin: action.isAdmin,
                isSuperAdmin: action.isSuperAdmin,
                userToken: action.token,
                user: action.user
            };
        }
        default: throw Error(`Unknown action: ${action.type}`);
    }
};

const useAdminAuth = () => {
    const [state, dispatch] = useReducer(reducer, defaultState);

    const history = useHistory();
    const location = useLocation();

    const { t } = useTranslation('admin_settings');
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (state.isLoggedIn) return;

        const { api } = getUrls();

        const redirectToLogin = (token?: string) => {
            const search = new URLSearchParams(location.search);

            const showOrgType = search.get('showOrgType') === 'true';
            const showExperimentalSettings = search.get('showExperimentalSettings') === 'true';

            const newState = createOauthState({
                user_uuid: token || uuid(),
                redirect: location.pathname,
                showOrgType: showOrgType || undefined,
                showExperimentalSettings: showExperimentalSettings || undefined
            });

            const params = new URLSearchParams({
                state: JSON.stringify(newState)
            });

            history.push(`/login?${params.toString()}`);
        };

        const getAuth = async (token: string) => {
            try {
                const { data } = await axios.get<AuthMeResponse>(`${api}${AUTH_ME}`, { headers: { token } });
                return data;
            } catch (err) {
                console.error(err);
                return false;
            }
        };

        const getUser = async () => {
            const token = localStorage.getItem('user_uuid');

            if (!token) {
                return redirectToLogin();
            }

            try {
                const user = await getAuth(token);

                if (!user) {
                    return redirectToLogin(token);
                }

                const isSuperAdmin = user.roles.includes(UserRole.SuperAdmin);
                const isAdmin = user.roles.includes(UserRole.Admin) || isSuperAdmin;

                dispatch({ type: 'login', isAdmin, isSuperAdmin, token, user });
            } catch (error) {
                enqueueSnackbar(t('error.userError'), { variant: 'error' });
            }
        };

        getUser();
    }, [enqueueSnackbar, history, location.pathname, location.search, state.isLoggedIn, t]);

    return {
        isLoading: state.isLoading,
        isLoggedIn: state.isLoggedIn,
        isAdmin: state.isAdmin,
        isSuperAdmin: state.isSuperAdmin,
        uuid: state.userToken
    };
};

const AdminAuthContainer = createContainer(useAdminAuth);

export default AdminAuthContainer;
